// ─────────────────────────────────────────────
//  breakDirective — the three documents that turn "the board knows somebody is
//  covering" into "the people know somebody is covering".
//
//  ⚠️ NAMING A COVER USED TO TELL NOBODY. `assignCoverToRoom` wrote the room doc
//  and stopped, and there is NO Firestore trigger on `rooms` — the triggers are
//  on assignments, tasks, alerts, users, staff and the sheet inbox. So the named
//  CRNA's phone never rang. The board drew a neat cyan "assigned" chip, and the
//  AIC still had to pick up a telephone: the exact cost this feature exists to
//  remove. The attending's measure of success is not adoption, it is whether the
//  break CRNA makes fewer phone calls.
//
//  ⚠️ WHY ITS OWN FILE. This composes `domain/tasks` (the document shape) with
//  `domain/directives` (the sentence). `directives` already imports `tasks` for
//  MAX_TASK_TEXT, so building it inside `tasks` would close an import CYCLE —
//  one that happens to resolve today, because every use is inside a function
//  body, and would break the moment either module grew work at module scope.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import { TASK_KINDS, AUTO_REASONS, MAX_TASK_TEXT } from "./tasks";
import { composeDirective } from "./directives";
import { uidOf } from "./channels";

/**
 * WHO the three directives go to, as auth uids.
 *
 * ⚠️ THIS RULE WAS HAND-WRITTEN IN BOTH CALLERS. `useRoomBreakActions` and the
 * Timeline BREAKS card each resolved the same three people with their own three
 * `uidOf` calls — and the supervisor rule in particular (room doc first, then
 * the day's row) is precisely the kind of thing that drifts when it lives in two
 * places. TimelineChartPage has NO test file, so a rule written there is a rule
 * nothing guards; the page's own comments say exactly that about
 * `domain/taskRecipients`.
 *
 * ⚠️ EVERY RESULT IS AN AUTH UID OR NULL. A staff doc id writes cleanly and
 * rings nobody, and `uidOf` returns null for somebody with no login — which is
 * the honest answer, and what makes `buildBreakCoverTasks` skip them instead of
 * addressing a ghost.
 *
 * @param {object}  opts
 * @param {Array}   opts.staff       the staff list (the uid link lives here)
 * @param {object}  opts.room        the room doc
 * @param {object} [opts.assignment] the day's row for that room, if known
 * @param {string}  opts.giverId     staff id of the person being asked to cover
 * @param {string} [opts.relievedId] staff id of the person being relieved;
 *                 defaults to the row's CRNA, then the room's provider
 */
export function breakCoverRecipients({
  staff = [],
  room = null,
  assignment = null,
  giverId = null,
  relievedId = null,
} = {}) {
  const relieved = relievedId || assignment?.ahpId || room?.providerId || null;
  return {
    giverUid: uidOf(staff, giverId),
    relievedUid: uidOf(staff, relieved),
    // The room doc is the standing fact; the day's row is the fallback for a
    // sheet-imported board, whose room docs carry no supervisor.
    supervisorUid: uidOf(staff, room?.supervisingDoctorId || assignment?.attendingId),
  };
}

/**
 * Build the directives for one named break cover.
 *
 * ⚠️ THREE RECIPIENTS, AND ONLY ONE OF THEM IS ASKED TO ANSWER.
 *   · the COVERER is instructed                      → requiresConfirm TRUE
 *   · the provider being relieved is informed        → FALSE
 *   · the room's supervising anaesthesiologist, too  → FALSE
 *
 * The attending was explicit that "both providers" is really three, because the
 * supervisor must know who is standing in their room — "quietly, on the
 * informational lane". And the acknowledgement that carries meaning is the
 * COVERER'S: only they can say "I am coming". Asking the relieved provider to
 * confirm as well would create two pending states for one event, so neither
 * would answer "is this room covered?" on its own, and one silence would raise
 * two escalations. One question, one answer, one thing that can go unanswered.
 *
 * ⚠️ THIS IS NOT AN ALARM, AND THE LANE IS THE COLLECTION, NOT A FIELD. `tasks`
 * carries no severity and must never gain one; it rides functions/taskPush onto
 * the `atc_tasks` channel at normal priority. Writing an `alert` for a break
 * would drop 14 rooms × 3 meals × 2 people ≈ 80 pushes a day into the same
 * surface as an emergency, and people who mute that surface miss the emergency.
 *
 * ⚠️ EVERY *Uid ARGUMENT MUST BE AN AUTH UID, never a staff doc id. A staff id
 * writes cleanly and rings nobody — resolve through `domain/channels.uidOf`
 * first. This is the failure that is invisible from both ends: the sender's
 * ledger reads "waiting" forever and the recipient was never addressed.
 *
 * @returns {Array<object>} 0..3 task docs ready for `createTask`. Empty when the
 *          board does not know enough to say anything true.
 */
export function buildBreakCoverTasks({
  facilityId,
  dateKey,
  senderId,
  senderName = "",
  roomName = null,
  channelLabel = "",
  giverName = "",
  giverUid = null,
  relievedName = "",
  relievedUid = null,
  supervisorUid = null,
} = {}) {
  if (!facilityId || !dateKey || !senderId) return [];

  const place = String(roomName || "").trim();
  const giver = String(giverName || "").trim();
  const relieved = String(relievedName || "").trim();
  const meal = String(channelLabel || "break").trim().toLowerCase();
  const sender = String(senderName || "").trim();

  const base = {
    facilityId,
    dateKey,
    senderId,
    senderName: sender,
    kind: TASK_KINDS.AUTO,
    autoReason: AUTO_REASONS.BREAK_COVER,
    roomName: place || null,
  };

  // Both halves of the shipped `break` verb, so an automatic directive reads in
  // exactly the same voice as one an operator types by hand. Re-deriving the
  // sentence here is how two surfaces start describing one event differently.
  const { text, counterpartText } = composeDirective({
    action: "break",
    counterpartName: relieved,
    recipientNames: giver ? [giver] : [],
    where: place,
  });

  const out = [];

  // 1. THE COVERER — the only one asked to answer.
  if (giverUid && text) {
    out.push({ ...base, recipientIds: [giverUid], text, requiresConfirm: true });
  }

  // 2. THE PROVIDER BEING RELIEVED — told, not asked.
  //
  // ⚠️ Gated on `counterpartText`, which `composeDirective` returns EMPTY when
  // there is no counterpart name. Falling back to the coverer's own instruction
  // ("Give a break in OR 5.") would reach the person RECEIVING the break as an
  // order to relieve themselves.
  if (relievedUid && counterpartText) {
    out.push({
      ...base,
      recipientIds: [relievedUid],
      text: counterpartText,
      requiresConfirm: false,
    });
  }

  // 3. THE SUPERVISING ANAESTHESIOLOGIST — a third sentence, because neither of
  // the other two is addressed to them. `tell` says "coming to give YOU a
  // break", which is simply false when the supervisor reads it, and `say` is an
  // instruction they are not being given.
  if (supervisorUid && giver && place) {
    const forMd = relieved
      ? `${giver} is covering ${relieved}'s ${meal} in ${place}.`
      : `${giver} is covering the ${meal} in ${place}.`;
    out.push({
      ...base,
      recipientIds: [supervisorUid],
      text: forMd.slice(0, MAX_TASK_TEXT),
      requiresConfirm: false,
    });
  }

  return out;
}

export default buildBreakCoverTasks;
